import type { FC, ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { cn } from 'src/utils/cn';

export const metaLabelColors = {
  success: cn(`
    text-foreground-meta-label-success
    legacy:text-[#5CB193]
  `),
  error: cn(`
    text-foreground-meta-label-error
    legacy:text-[#E86161]
  `),
  pending: cn(`
    text-foreground-meta-label-pending
    legacy:text-[#B3986A]
  `),
  inactive: cn(`
    text-foreground-meta-label-inactive
    legacy:text-[#7C7C7C]
  `),
  entry: cn(`
    text-foreground-meta-label-entry
    legacy:text-[#56A7D8]
  `),
  exit: cn(`
    text-foreground-meta-label-exit
    legacy:text-[#CA85AC]
  `),
  bundle: cn(`
    text-foreground-meta-label-bundle
    legacy:text-[#7879B3]
  `),
  primary: cn(`
    text-foreground-meta-label-primary
    legacy:text-[#009CA7]
  `),
  secondary: cn(`
    text-foreground-meta-label-secondary
    legacy:text-[#3E8500]
  `),
  info: cn(`
    text-foreground-meta-label-info
    legacy:text-[#0a5f65]
  `),
  own: cn(`
    text-foreground-meta-label-own
    legacy:text-[#D27F35]
  `),
  ready: cn(`
    text-foreground-meta-label-ready
    legacy:text-[#56a7d8]
  `),
} as const;

export type MetaLabelColor = keyof typeof metaLabelColors;

export type MetaLabelProps = {
  color: MetaLabelColor;
  children: ReactNode;
  'data-testid'?: string;
  className?: string;
};

export const MetaLabel: FC<MetaLabelProps> = ({
  color,
  children,
  ['data-testid']: testId = 'meta-label',
  className,
}) => {
  const contextMode = useMetaLabelMode();
  return (
    <span
      className={cn(
        'font-label-semibold',
        contextMode === 'page'
          ? `
            text-label-md
            legacy:text-[1.5rem]
          `
          : `
            text-label-sm
            legacy:text-[1.2rem]
          `,
        metaLabelColors[color],
        className
      )}
      data-testid={testId}
    >
      {children}
    </span>
  );
};

type MetaLabelsProps = {
  children: ReactNode;
  'data-testid'?: string;
};

export const MetaLabels: FC<MetaLabelsProps> = ({
  children,
  ['data-testid']: testId = 'meta-labels',
}) => {
  const contextMode = useMetaLabelMode();
  return (
    <div
      className={cn(
        'flex flex-row flex-wrap',
        contextMode === 'page'
          ? `
            gap-lg
            legacy:gap-[1.2rem]
          `
          : `
            gap-md
            legacy:gap-[1rem]
          `
      )}
      data-testid={testId}
    >
      {children}
    </div>
  );
};

// Provider
type MetaLabelMode = 'page' | 'standalone';

const MetaLabelContext = createContext<MetaLabelMode>('standalone');

export function useMetaLabelMode() {
  return useContext(MetaLabelContext);
}

export const MetaLabelProvider = MetaLabelContext.Provider;
